import * as React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import Button from './Button';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

describe('Button Component', () => {
  const mockOnClick = jest.fn();

  beforeEach(() => {
    mockOnClick.mockClear();
  });

  describe('Rendering as button', () => {
    it('renders with default props', () => {
      render(<Button onClick={mockOnClick}>Test Button</Button>);

      const button = screen.getByRole('button');
      expect(button).toBeInTheDocument();
      expect(button).toHaveTextContent('Test Button');
      expect(button).toHaveAttribute('data-context', 'neutral');
      expect(button).toHaveAttribute('data-type', 'primary');
      expect(button).toHaveAttribute('data-size', 'M');
    });

    it('renders with custom props', () => {
      render(
        <Button
          onClick={mockOnClick}
          context="company"
          type="secondary"
          size="L"
          disabled
        >
          Custom Button
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toHaveTextContent('Custom Button');
      expect(button).toHaveAttribute('data-context', 'company');
      expect(button).toHaveAttribute('data-type', 'secondary');
      expect(button).toHaveAttribute('data-size', 'L');
      expect(button).toHaveAttribute('disabled');
      expect(button).toBeDisabled();
    });

    it('calls onClick when clicked', () => {
      render(<Button onClick={mockOnClick}>Click me</Button>);

      const button = screen.getByRole('button');
      fireEvent.click(button);

      expect(mockOnClick).toHaveBeenCalledTimes(1);
    });

    it('does not call onClick when disabled', () => {
      render(
        <Button onClick={mockOnClick} disabled>
          Disabled Button
        </Button>
      );

      const button = screen.getByRole('button');
      fireEvent.click(button);

      expect(mockOnClick).not.toHaveBeenCalled();
    });

    it('renders as submit button', () => {
      render(
        <Button onClick={mockOnClick} typeAttr="submit">
          Submit
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toHaveAttribute('type', 'submit');
    });
  });

  describe('Rendering as link', () => {
    it('renders as anchor tag when href is provided', () => {
      render(<Button href="/profile">Go to Profile</Button>);

      const link = screen.getByRole('link');
      expect(link).toBeInTheDocument();
      expect(link).toHaveTextContent('Go to Profile');
      expect(link).toHaveAttribute('href', '/profile');
    });

    it('renders link with target and rel attributes', () => {
      render(
        <Button href="https://example.com" target="_blank">
          External Link
        </Button>
      );

      const link = screen.getByRole('link');
      expect(link).toHaveAttribute('href', 'https://example.com');
      expect(link).toHaveAttribute('target', '_blank');
      expect(link).toHaveAttribute('rel', 'noopener noreferrer');
    });

    it('renders link with custom rel attribute', () => {
      render(
        <Button href="/page" target="_blank" rel="nofollow">
          Custom Rel
        </Button>
      );

      const link = screen.getByRole('link');
      expect(link).toHaveAttribute('rel', 'nofollow');
    });

    it('applies all styling props to link', () => {
      render(
        <Button
          href="/jobs"
          context="company"
          type="secondary"
          size="M"
          data-testid="link-button"
        >
          View Jobs
        </Button>
      );

      const link = screen.getByTestId('link-button');
      expect(link).toHaveAttribute('data-context', 'company');
      expect(link).toHaveAttribute('data-type', 'secondary');
      expect(link).toHaveAttribute('data-size', 'M');
    });
  });

  describe('Icon rendering', () => {

    it('renders with left icon', () => {
      render(
        <Button onClick={mockOnClick} iconLeft='arrowLeft'>
          Back
        </Button>
      );

      const button = screen.getByRole('button');
      const icon = button.querySelector('span.iconLeft > span');
      expect(icon).toBeTruthy();
    });

    it('renders with right icon', () => {
      render(
        <Button onClick={mockOnClick} iconRight='eye'>
          Next
        </Button>
      );

      const button = screen.getByRole('button');
      const icon = button.querySelector('span.iconRight > span');
      expect(icon).toBeTruthy();
    });

    it('applies single icon class for icon-only buttons', () => {
      render(
        <Button onClick={mockOnClick} iconLeft="arrowLeft">
          {null}
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toHaveClass('singleIcon');
    });

    it('does not apply single icon class when text is present', () => {
      render(
        <Button onClick={mockOnClick} iconLeft='arrowLeft'>
          Back
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).not.toHaveClass('singleIcon');
    });

    it('does not apply single icon class when both icons are present', () => {
      render(
        <Button onClick={mockOnClick} iconLeft='arrowLeft' iconRight='arrowRight'>
          {null}
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).not.toHaveClass('singleIcon');
    });

    it('renders with both icons', () => {
      render(
        <Button onClick={mockOnClick} iconLeft='eyeClosed' iconRight='eye'>
          Both Icons
        </Button>
      );

      const button = screen.getByRole('button');
      const icons = button.querySelectorAll('span.iconLeft > span, span.iconRight > span');
      expect(icons).toHaveLength(2);
    });

    it('renders icons on links', () => {
      render(
        <Button href="/profile" iconLeft="user" iconRight="chevronRight">
          Profile
        </Button>
      );

      const link = screen.getByRole('link');
      const icons = link.querySelectorAll('span.iconLeft > span, span.iconRight > span');
      expect(icons).toHaveLength(2);
    });
  });

  describe('Styling and classes', () => {

    it('applies custom className', () => {
      render(
        <Button onClick={mockOnClick} className="custom-class">
          Custom Class
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toHaveClass('custom-class');
    });

    it('sets data-testid', () => {
      render(
        <Button onClick={mockOnClick} data-testid="test-button">
          Test
        </Button>
      );

      expect(screen.getByTestId('test-button')).toBeInTheDocument();
    });
  });

  describe('Type variants', () => {

    const buttonTypes = ['primary', 'secondary', 'terceary'] as const;

    buttonTypes.forEach(type => {
      it(`renders with type ${type}`, () => {
        render(
          <Button onClick={mockOnClick} type={type}>
            {type} Button
          </Button>
        );

        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-type', type);
      });
    });
  });

  describe('Size variants', () => {

    const buttonSizes = ['S', 'M', 'L'] as const;

    buttonSizes.forEach(size => {
      it(`renders with size ${size}`, () => {
        render(
          <Button onClick={mockOnClick} size={size}>
            Size {size}
          </Button>
        );

        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-size', size);
      });
    });
  });

  describe('Context variants', () => {

    const buttonContexts = ['talent', 'company', 'neutral'] as const;

    buttonContexts.forEach(context => {
      it(`renders with context ${context}`, () => {
        render(
          <Button onClick={mockOnClick} context={context}>
            {context} Button
          </Button>
        );

        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-context', context);
      });
    });
  });

  describe('Accessibility', () => {
    it('renders aria-label on button', () => {
      render(<Button onClick={mockOnClick} iconLeft="search" aria-label="Search" />);
      expect(screen.getByRole('button', { name: 'Search' })).toBeInTheDocument();
    });

    it('renders aria-label on link', () => {
      render(<Button href="/search" iconLeft="search" aria-label="Search" />);
      expect(screen.getByRole('link', { name: 'Search' })).toBeInTheDocument();
    });

    it('marks icons as aria-hidden', () => {
      const { container } = render(
        <Button onClick={mockOnClick} iconLeft="search">Find</Button>
      );
      const icon = container.querySelector('[data-icon="search"]');
      expect(icon).toHaveAttribute('aria-hidden', 'true');
    });

    it('does not set data-spinner when spinner is false', () => {
      render(<Button onClick={mockOnClick}>Click</Button>);
      const button = screen.getByRole('button');
      expect(button).not.toHaveAttribute('data-spinner');
    });

    it('sets data-spinner when spinner is true', () => {
      render(<Button onClick={mockOnClick} spinner>Loading</Button>);
      const button = screen.getByRole('button');
      expect(button).toHaveAttribute('data-spinner', 'true');
    });

    it('renders id on button', () => {
      render(<Button onClick={mockOnClick} id="my-btn">Click</Button>);
      expect(screen.getByRole('button')).toHaveAttribute('id', 'my-btn');
    });

    it('renders id on link', () => {
      render(<Button href="/page" id="my-link">Go</Button>);
      expect(screen.getByRole('link')).toHaveAttribute('id', 'my-link');
    });

    describe('Disabled link', () => {
      it('has aria-disabled=true', () => {
        const { container } = render(<Button href="/page" disabled>Disabled Link</Button>);
        const link = container.querySelector('a')!;
        expect(link).toHaveAttribute('aria-disabled', 'true');
      });

      it('has tabIndex=-1', () => {
        const { container } = render(<Button href="/page" disabled>Disabled Link</Button>);
        const link = container.querySelector('a')!;
        expect(link).toHaveAttribute('tabIndex', '-1');
      });

      it('does not have href', () => {
        const { container } = render(<Button href="/page" disabled>Disabled Link</Button>);
        const link = container.querySelector('a')!;
        expect(link).not.toHaveAttribute('href');
      });

      it('prevents default on click', () => {
        const { container } = render(<Button href="/page" disabled>Disabled Link</Button>);
        const link = container.querySelector('a')!;
        const defaultPrevented = !fireEvent.click(link);
        expect(defaultPrevented).toBe(true);
      });
    });
  });
});
